import { useRef, useState, useCallback } from "react";

// Hover magnifier for the product detail image. A circular lens follows the
// cursor and shows a 2.5x view of the same image (native pixels carry the zoom;
// products are 1080px+). Fine-pointer devices only — touch/coarse pointers get
// a plain <img>, since the effect is a hover interaction with no cursor to track.

const LENS_SIZE = 200; // lens diameter, px
const ZOOM = 2.5; // magnification factor

function detectFinePointer() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
}

export default function ZoomImage({ src, alt }) {
  // Lazy init so the magnifier is live on first paint (no plain-img flash) when
  // the device supports it. Evaluated once; pointer type doesn't change at runtime.
  const [fine] = useState(detectFinePointer);
  const imgRef = useRef(null);
  const [lens, setLens] = useState(null);

  const handleMove = useCallback((e) => {
    const img = imgRef.current;
    if (!img) return;
    const ir = img.getBoundingClientRect();
    const wr = e.currentTarget.getBoundingClientRect();
    // Cursor position within the rendered image. The image is `contain`-fit and
    // centered, so the wrapper has white margins around it.
    const x = e.clientX - ir.left;
    const y = e.clientY - ir.top;
    const r = LENS_SIZE / 2;
    // Free-follow, no clamp, no switch-off: the lens tracks the cursor exactly and
    // stays visible as long as the pointer is anywhere over the media box. Near or
    // past the image edge it overhangs, and the area beyond the image shows the
    // white backing. The lens only disappears on mouse-leave of the media box.
    setLens({
      // Position relative to the wrapper, accounting for the image's offset
      // within it (centered, so there may be margin on one axis).
      left: ir.left - wr.left + x,
      top: ir.top - wr.top + y,
      bgW: ir.width * ZOOM,
      bgH: ir.height * ZOOM,
      // Place the scaled image so the point under the cursor sits centered in
      // the lens: offset = r - (point * zoom).
      bgX: r - x * ZOOM,
      bgY: r - y * ZOOM,
    });
  }, []);

  const handleLeave = useCallback(() => setLens(null), []);

  if (!fine) {
    return <img src={src} alt={alt} />;
  }

  return (
    <div
      className={`pmp-zoom${lens ? " is-active" : ""}`}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
    >
      <img ref={imgRef} src={src} alt={alt} draggable="false" />
      {lens ? (
        <div
          className="pmp-zoom-lens"
          style={{
            width: LENS_SIZE,
            height: LENS_SIZE,
            left: lens.left,
            top: lens.top,
            backgroundImage: `url("${src}")`,
            backgroundSize: `${lens.bgW}px ${lens.bgH}px`,
            backgroundPosition: `${lens.bgX}px ${lens.bgY}px`,
          }}
        />
      ) : null}
    </div>
  );
}
